/**
 * Helper per news/promozioni.
 */

import DOMPurify from 'dompurify'
import type { News } from '@/lib/graphql/types'

/**
 * Genera una chiave stabile per identificare una news.
 * Lo schema `New` non ha un `id`: il mobile usa l'indice dell'array (fragile
 * se la lista cambia), noi preferiamo `datetime+title` (collision-free per
 * articoli reali).
 */
export function newsKey(item: News): string {
  // Chiave grezza datetime+title. NON encodiamo qui: l'encoding nell'URL lo
  // gestisce <Link>/router e useParams() decodifica automaticamente al ritorno.
  // Encodare qui causerebbe un mismatch (encoded vs decoded) in findNewsByKey,
  // con l'articolo che risulta "non disponibile".
  return `${item.datetime}__${item.title}`
}

export function findNewsByKey(items: News[] | undefined, key: string): News | undefined {
  return items?.find((n) => newsKey(n) === key)
}

/**
 * Sanitizza l'HTML in arrivo dal backend. Rimuove `<script>`, event handler
 * inline, link `javascript:`, etc. Permette tag testuali base + img + link.
 */
export function sanitizeNewsHtml(html: string | null | undefined): string {
  if (!html) return ''
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p',
      'br',
      'strong',
      'em',
      'b',
      'i',
      'u',
      'a',
      'ul',
      'ol',
      'li',
      'h1',
      'h2',
      'h3',
      'h4',
      'h5',
      'h6',
      'blockquote',
      'img',
      'figure',
      'figcaption',
      'span',
      'div',
    ],
    ALLOWED_ATTR: ['href', 'target', 'rel', 'src', 'alt', 'title', 'class'],
    ADD_ATTR: ['target'],
  })
}
